import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import api from './services/api';
import Login from './components/Login/Login';
import Dashboard from './components/Dashboard/Dashboard';
import Sidebar from './components/SideBar/SideBar';
import Header from './components/Header/Header';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';

import ProtectedRoute from './services/ProtectedRoute';

import ListCategories from './components/Category/ListCategories/ListCategories';
import AddCategory from './components/Category/AddCategory/AddCategory';

import AddCourse from './components/Course/AddCourse/AddCourse';
import ListCourses from './components/Course/ListCourses/ListCourses';

import ListStudent from './components/Student/ListStudent/ListStudent';
import AddStudent from './components/Student/AddStudent/AddStudent';

import UserManager from './components/User/UserManager/UserManager';
import ListUsers from './components/User/ListUser/ListUsers';

import Attendance from './components/Attendance/AttendanceList/AttendanceList';
import AttendanceRecords from './components/Attendance/AttendanceRecords/AttendanceRecords';
import ListRequest from './components/Requests/ListRequest/ListRequest';
import ReportPage from './components/Reports/ReportPage';
import Settings from './components/Settings/Settings';

import HomeNutricionista from './components/Nutricionista/HomeNutricionista';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userName, setUserName] = useState(null);
  const [poloName, setPoloName] = useState(null);
  const [hasRequests, setHasRequests] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hideHeader, setHideHeader] = useState(false);
  useEffect(() => {
    const handleRequestsStatusUpdate = (event) => {
      setHasRequests(event.detail);
    };
    window.addEventListener('requestsStatusUpdate', handleRequestsStatusUpdate);
    return () => {
      window.removeEventListener('requestsStatusUpdate', handleRequestsStatusUpdate);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    // Verifica se o usuário está autenticado ao montar o app
    api.get('/user')
      .then(response => {
        if (response.data) {
          setIsLoggedIn(true);
          setUserType(response.data.type);
          setUserName(response.data.name);
          // Corrige para buscar o polo do user, se existir, ou tenta buscar novamente
          if (response.data.polo && response.data.polo.name) {
            setPoloName(response.data.polo.name);
          } else if (response.data.poloName) {
            setPoloName(response.data.poloName);
          } else {
            setPoloName('');
          }
        } else {
          setIsLoggedIn(false);
          setUserType(null);
          setUserName(null);
          setPoloName(null);
          localStorage.clear();
          sessionStorage.clear();
        }
      })
      .catch(() => {
        setIsLoggedIn(false);
        setUserType(null);
        setUserName(null);
        setPoloName(null);
        localStorage.clear();
        sessionStorage.clear();
      })
      .finally(() => {
        setIsAuthLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isLoggedIn && !poloName) {
      // Tenta buscar o polo selecionado do usuário
      api.get('/user/polos-by-user')
        .then(response => {
          if (response.data && response.data.polos && response.data.polos.length > 0) {
            // Busca o ID do polo salvo no localStorage
            const savedPoloId = localStorage.getItem('selectedPoloId');
            let selectedPolo = null;
            if (savedPoloId) {
              selectedPolo = response.data.polos.find(p => String(p.id) === String(savedPoloId));
            }
            if (!selectedPolo) {
              selectedPolo = response.data.polos.find(p => p.selected) || response.data.polos[0];
            }
            setPoloName(selectedPolo?.name || '');
          }
        })
        .catch(() => {
          // Não faz nada se der erro
        });
    }
  }, [isLoggedIn, poloName]);

  const toggleSidebar = () => setIsSidebarOpen(prevState => !prevState);
  const handleMouseEnter = () => setIsSidebarOpen(true);
  const handleMouseLeave = () => setIsSidebarOpen(false);

  const handleLoginSuccess = (user, polo) => {
    setIsLoggedIn(true);
    setUserType(user.type);
    setUserName(user.name);
    // Salva o ID do polo escolhido
    if (polo?.id) {
      localStorage.setItem('selectedPoloId', polo.id);
    }
    setPoloName(polo?.name || user.polo?.name || user.poloName || '');
    setIsSidebarOpen(false);
  };

  const renderProtectedRoute = (path, Component, requiredUserType = "admin") => (
    <Route
      path={path}
      element={<ProtectedRoute isLoggedIn={isLoggedIn} userType={userType} requiredUserType={requiredUserType} element={React.cloneElement(Component, { userType, userName })} />}
    />
  );

  const isNutricionista = userType === 'nutricionista';
  const shouldShowSidebar = isLoggedIn && !isMobile && !isNutricionista;

  const getInitialRedirect = () => {
    if (userType === 'professor') return '/chamadas';
    if (userType === 'nutricionista') return '/nutricionista';
    return '/dashboard';
  };

  return (
    <Router>
      {isAuthLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <LoadingSpinner />
        </div>
      ) : (
        isLoggedIn && isNutricionista ? (
          <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            <Header userName={userName} userType={userType} poloName={poloName} setIsLoggedIn={setIsLoggedIn} hideHeader={hideHeader} />
            <div className="main-content">
              <Routes>
                <Route path="/" element={<Navigate to="/nutricionista" replace />} />
                <Route path="/nutricionista" element={<HomeNutricionista userType={userType} userName={userName} setIsLoggedIn={setIsLoggedIn} />} />
                <Route path="*" element={<Navigate to="/nutricionista" replace />} />
              </Routes>
            </div>
          </div>
        ) : isMobile && isLoggedIn ? (
          <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: '#333' }}>
            <Header userName={userName} userType={userType} poloName={poloName} setIsLoggedIn={setIsLoggedIn} hideHeader={hideHeader} />
            <div className="main-content" style={{ width: '100%', maxWidth: 400, background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.2)', margin: 16, padding: 0 }}>
              <AttendanceRecords />
            </div>
          </div>
        ) : (
          <div className={`app-container${isLoggedIn ? (isSidebarOpen ? ' sidebar-open' : ' sidebar-closed') : ''}`}>
            {shouldShowSidebar && (
              <Sidebar
                isOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
                setIsLoggedIn={setIsLoggedIn}
                userType={userType}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                hasRequests={hasRequests}
              />
            )}
            {isLoggedIn && (
              <Header userName={userName} userType={userType} poloName={poloName} setIsLoggedIn={setIsLoggedIn} hideHeader={hideHeader} />
            )}
            <div className="main-content">
              <Routes>
                <Route
                  path="/"
                  element={isLoggedIn ? <Navigate to={getInitialRedirect()} replace /> : <Login setIsLoggedIn={handleLoginSuccess} />}
                />
                {renderProtectedRoute("/chamadas", <Attendance userType={userType} />, ['admin', 'professor'])}
                {renderProtectedRoute("/dashboard", <Dashboard />)}
                {renderProtectedRoute("/listar-oficinas", <ListCategories />)}
                {renderProtectedRoute("/criar-oficina", <AddCategory />)}
                {renderProtectedRoute("/criar-aula", <AddCourse />)}
                {renderProtectedRoute("/adicionar-turma", <AddCourse />)}
                {renderProtectedRoute("/listar-turmas", <ListCourses />)}
                {renderProtectedRoute("/adicionar-aluno", <AddStudent />)}
                {renderProtectedRoute("/alunos", <ListStudent hideHeader={hideHeader} setHideHeader={setHideHeader} userName={userName} />, ['admin', 'nutricionista'])}
                {renderProtectedRoute("/adicionar-usuario", <UserManager />)}
                {renderProtectedRoute("/listar-usuarios", <ListUsers />)}
                {renderProtectedRoute("/solicitacoes", <ListRequest />)}
                {renderProtectedRoute("/relatorios", <ReportPage userName={userName} />, 'admin')}
                {renderProtectedRoute("/settings", <Settings />, ['admin', 'nutricionista'])}
                {renderProtectedRoute("/nutricionista", <HomeNutricionista />, 'nutricionista')}
              </Routes>
            </div>
          </div>
        )
      )}
    </Router>
  );
}

export default App;